/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable, signal } from '@angular/core';
import { AppConfigService } from '@alfresco/adf-core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi } from '@alfresco/js-api';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AcaConfigFolderService } from '../shared/aca-config-folder.service';

interface Branding {
  name: string;
  /** A data URI, or an empty string when the shipped asset is in use. */
  logo: string;
  /** `#rrggbb`, or an empty string to keep the stylesheet's own colour. */
  primary?: string;
}

/**
 * The system's name and logo, as an administrator set them.
 *
 * `app.config.json` carries both, but that file is baked into the build: the
 * only way to change either was to edit it and deploy again. They live instead
 * in one small document in the repository — `Data Dictionary/ACA/branding.json`
 * — which every signed-in user can read (Data Dictionary grants Consumer to
 * GROUP_EVERYONE) and only an administrator can write. The configuration is
 * still the fallback, so a deployment that never sets a brand behaves exactly
 * as it did before.
 *
 * The logo is held in that document as a data URI rather than as a node of its
 * own: an image node's URL needs an authentication ticket, which would have to
 * be threaded through the login screen where nobody is signed in yet.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly api = inject(AlfrescoApiService);
  private readonly configFolder = inject(AcaConfigFolderService);
  private readonly config = inject(AppConfigService);

  private static readonly FOLDER_PATH = 'Data Dictionary/ACA';
  private static readonly FILE_NAME = 'branding.json';

  /** Above this a logo is refused: it travels inside the document itself. */
  static readonly MAX_LOGO_BYTES = 512 * 1024;

  /** Remembers the brand for the sign-in screen, where nobody can read the
      repository yet. It is only ever a copy of what was last read from it. */
  private static readonly CACHE_KEY = 'aca.branding';

  readonly name = signal('');
  readonly logo = signal('');
  /** Whether the logo on screen is the uploaded one or the shipped asset. */
  readonly customLogo = signal(false);
  /** The brand colour in force; empty means the stylesheet's own. */
  readonly primary = signal('');

  /** The colour the stylesheet starts from, offered as one of the choices. */
  static readonly DEFAULT_PRIMARY = '#2563eb';

  /** A palette to pick from; any other colour can still be typed in. */
  static readonly PRESETS = ['#2563eb', '#4f46e5', '#0891b2', '#0d9488', '#16a34a', '#ca8a04', '#dc2626', '#db2777', '#475569'];

  private loaded = false;

  constructor() {
    this.apply(this.cached() ?? { name: this.configuredName(), logo: '' });
  }

  configuredName(): string {
    return this.config.get<string>('application.name', 'Alfresco') || 'Alfresco';
  }

  configuredLogo(): string {
    return this.config.get<string>('application.logo', './assets/images/alfresco-logo.svg');
  }

  /**
   * Reads the brand once per session.
   *
   * Called from the shell, which only exists once someone is signed in — the
   * document sits in the repository and is not readable before that.
   */
  load(): void {
    if (this.loaded) {
      return;
    }
    this.loaded = true;

    this.read()
      .pipe(catchError(() => of(null)))
      .subscribe((branding) => {
        if (branding) {
          this.apply(branding);
        }
      });
  }

  /** Writes the brand for everyone. Requires an administrator. */
  save(name: string, logo: string | null, primary?: string): Observable<void> {
    const branding: Branding = { name: name.trim() || this.configuredName(), logo: logo ?? '', primary: primary ?? '' };

    return this.nodeId(true).pipe(
      switchMap((nodeId) => from(this.nodes().updateNodeContent(nodeId, JSON.stringify(branding)) as Promise<any>)),
      tap(() => this.apply(branding)),
      map(() => undefined)
    );
  }

  private apply(branding: Branding): void {
    this.name.set(branding.name || this.configuredName());
    this.logo.set(branding.logo || this.configuredLogo());
    this.customLogo.set(!!branding.logo);
    this.primary.set(branding.primary || '');
    BrandingService.paint(branding.primary || '');

    try {
      localStorage.setItem(BrandingService.CACHE_KEY, JSON.stringify(branding));
    } catch {
      // a browser that refuses storage simply shows the configured brand on the
      // sign-in screen and the real one everywhere behind it
    }
  }

  private cached(): Branding | null {
    try {
      const raw = localStorage.getItem(BrandingService.CACHE_KEY);
      return raw ? (JSON.parse(raw) as Branding) : null;
    } catch {
      return null;
    }
  }

  private read(): Observable<Branding | null> {
    return this.nodeId(false).pipe(
      switchMap((nodeId) => (nodeId ? from(this.nodes().getNodeContent(nodeId) as Promise<any>) : of(null))),
      switchMap((content: any) => (content ? this.text(content) : of(null))),
      map((text) => (text ? (JSON.parse(text) as Branding) : null))
    );
  }

  /** A Blob on the browser, but the API's typings promise only `any`. */
  private text(content: any): Observable<string | null> {
    if (typeof content === 'string') {
      return of(content);
    }
    if (content instanceof Blob) {
      return from(content.text());
    }
    return of(null);
  }

  /**
   * The document's node id, creating the folder and the document on the way
   * when `create` is set — which only ever happens on an administrator's save.
   */
  private nodeId(create: boolean): Observable<string | null> {
    const nodes = this.nodes();

    // listed once for every settings document — see AcaConfigFolderService
    return this.configFolder.find(BrandingService.FILE_NAME).pipe(
      catchError(() => {
        if (!create) {
          return of(null);
        }

        // the folder may or may not be there; either way the document is not
        return from(nodes.createNode('-root-', { name: 'ACA', nodeType: 'cm:folder', relativePath: 'Data Dictionary' } as any) as Promise<any>).pipe(
          catchError(() => of(null)),
          switchMap(() =>
            from(
              nodes.createNode('-root-', {
                name: BrandingService.FILE_NAME,
                nodeType: 'cm:content',
                relativePath: BrandingService.FOLDER_PATH
              } as any) as Promise<any>
            )
          ),
          map((entry: any) => entry?.entry?.id as string),
          tap(() => this.configFolder.forget())
        );
      })
    );
  }

  private nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  /**
   * Puts the brand colour on the document root.
   *
   * Everything blue in this application resolves through these few custom
   * properties, so setting them here recolours the whole interface — buttons,
   * the active navigation pill, links, focus rings, the dashboard banner. The
   * shades either side of the chosen colour are derived rather than asked for:
   * one colour to pick, and the hover, tint and gradient stay in proportion to
   * it. An empty colour removes the overrides and the stylesheet's own values
   * come back.
   */
  static paint(primary: string): void {
    const root = document.documentElement;
    const names = [
      ...BrandingService.OWN_TOKENS,
      ...BrandingService.LIBRARY_TOKENS,
      '--aca-brand-gradient',
      '--aca-focus-ring',
      '--theme-selected-background-color'
    ];

    if (!BrandingService.isHex(primary)) {
      names.forEach((name) => root.style.removeProperty(name));
      return;
    }

    const [r, g, b] = BrandingService.rgb(primary);
    const mix = (channel: number, target: number, amount: number) => Math.round(channel + (target - channel) * amount);
    const hex = (parts: number[]) => '#' + parts.map((part) => part.toString(16).padStart(2, '0')).join('');

    const hover = hex([r, g, b].map((c) => mix(c, 0, 0.16)));
    const soft = hex([r, g, b].map((c) => mix(c, 255, 0.92)));
    const light = hex([r, g, b].map((c) => mix(c, 255, 0.1)));
    const deep = hex([r, g, b].map((c) => mix(c, 0, 0.28)));

    root.style.setProperty('--aca-color-primary', primary);
    root.style.setProperty('--aca-color-primary-hover', hover);
    root.style.setProperty('--aca-color-primary-soft', soft);
    root.style.setProperty('--aca-brand-gradient', `linear-gradient(135deg, ${light} 0%, ${deep} 100%)`);
    root.style.setProperty('--aca-focus-ring', `0 0 0 3px rgba(${r}, ${g}, ${b}, 0.24)`);
    root.style.setProperty('--theme-selected-background-color', `rgba(${r}, ${g}, ${b}, 0.3)`);

    // ADF and Material read their own colour from tokens the compiled theme
    // baked at build time; the ones this application actually shows are pointed
    // at the chosen colour so the brand does not stop at our own components.
    BrandingService.LIBRARY_TOKENS.forEach((token) => root.style.setProperty(token, primary));
  }

  /** This application's own brand properties. */
  private static readonly OWN_TOKENS = ['--aca-color-primary', '--aca-color-primary-hover', '--aca-color-primary-soft'];

  /** ADF and Material tokens that are visible on these screens. */
  private static readonly LIBRARY_TOKENS = [
    '--theme-primary-color',
    '--theme-blue-button-color',
    '--theme-blue-checkbox-color',
    '--mdc-outlined-text-field-focus-outline-color',
    '--mdc-outlined-text-field-caret-color',
    '--mdc-filled-text-field-focus-active-indicator-color',
    '--mdc-filled-text-field-caret-color',
    '--mat-option-selected-state-label-text-color',
    '--mdc-circular-progress-active-indicator-color',
    '--mat-badge-background-color',
    '--mat-datepicker-calendar-date-selected-state-background-color',
    '--mat-datepicker-toggle-active-state-icon-color'
  ];

  static isHex(value: string): boolean {
    return /^#[0-9a-fA-F]{6}$/.test(value);
  }

  private static rgb(hex: string): [number, number, number] {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  }
}
